import {
  DisconnectOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  message,
  Select,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { PermissionKey } from "../../../constants/permissions";
import { PERMISSIONS } from "../../../constants/permissions";
import { deviceApi } from "../../../services/api";
import { RootState } from "../../../store";
import { DeviceListResponse } from "../../../types/device.types";

const { Option } = Select;

const DeviceListPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState<DeviceListResponse[]>([]);
  const [releasingId, setReleasingId] = useState<number | null>(null);
  const [togglingReminderId, setTogglingReminderId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { permissions, user } = useSelector((state: RootState) => state.auth);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [form] = Form.useForm();

  // View device modal state
  const [selectedDevice, setSelectedDevice] =
    useState<DeviceListResponse | null>(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);

  const [statusFilter, setStatusFilter] = useState<string>("");

  const isAdmin = user?.designation?.toUpperCase().includes("ADMIN");

  const hasPermission = (permissionKey: PermissionKey) => {
    return permissions.some((p) => p.name === permissionKey);
  };

  useEffect(() => {
    fetchDevices(pagination, activeOnly, searchText, statusFilter);
  }, []);

  const fetchDevices = async (
    pagination: any,
    active: boolean,
    searchText: string,
    status?: string
  ) => {
    try {
      setLoading(true);

      const filter: any = { active, status };

      if (searchText) {
        filter.sellerOrganization = searchText;
        filter.sellerPhone = searchText;
        filter.deviceId = searchText;
        filter.customerName = searchText;
        filter.customerPhone = searchText;
        filter.deviceModel = searchText;
      }

      const response = await deviceApi.getDevices({
        pageNumber: pagination.current - 1,
        pageSize: pagination.pageSize,
        filter,
      });

      if (response.status === "SUCCESS") {
        setDevices(response.data);
        setPagination({
          ...pagination,
          total: response.totalElements,
        });
      } else {
        message.error(response.message || "Failed to fetch devices");
      }
    } catch (error) {
      message.error("Failed to fetch devices");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({
      ...pagination,
      current: 1,
    });
    fetchDevices(pagination, activeOnly, searchText, statusFilter);
  };

  const handleRelease = async (id: number) => {
    try {
      setReleasingId(id);
      const response = await deviceApi.releaseDevice(id);
      if (response.status === "SUCCESS") {
        message.success("Device released successfully");
        fetchDevices(pagination, activeOnly, searchText, statusFilter);
      } else {
        message.error(response.message || "Failed to release device");
      }
    } catch (error) {
      message.error("Failed to release device");
    } finally {
      setReleasingId(null);
    }
  };

  const handleToggleReminder = async (deviceId: string, enabled: boolean) => {
    try {
      setTogglingReminderId(deviceId);
      const response = await deviceApi.toggleReminderActive(deviceId, enabled);
      if (response.status === "SUCCESS") {
        setDevices((prev) =>
          prev.map((d) =>
            d.deviceId === deviceId ? { ...d, reminderActive: enabled } : d
          )
        );
        if (selectedDevice?.deviceId === deviceId) {
          setSelectedDevice((prev) => prev ? { ...prev, reminderActive: enabled } : prev);
        }
      } else {
        message.error(response.message || "Failed to toggle auto reminder");
      }
    } catch (error) {
      message.error("Failed to toggle auto reminder");
    } finally {
      setTogglingReminderId(null);
    }
  };

  const handleTableChange = (pagination: any) => {
    setPagination(pagination);
    fetchDevices(pagination, activeOnly, searchText, statusFilter);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPagination({
      ...pagination,
      current: 1,
    });
    fetchDevices(pagination, activeOnly, searchText, value);
  };

  // View device details handler
  const handleViewDevice = (device: DeviceListResponse) => {
    setSelectedDevice(device);
    setIsViewModalVisible(true);
  };

  const columns: ColumnsType<DeviceListResponse> = [
    {
      title: "Device Model",
      dataIndex: "deviceModel",
      key: "deviceModel",
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
    },
    {
      title: "Customer Phone",
      dataIndex: "customerPhone",
      key: "customerPhone",
    },
    {
      title: "Seller",
      dataIndex: "sellerOrganization",
      key: "sellerOrganization",
    },
    {
      title: "Registered At",
      dataIndex: "createdAt",
      key: "createdAt",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const color =
          status === "ACTIVE"
            ? "success"
            : status === "PENDING"
            ? "warning"
            : status === "LOCKED"
            ? "error"
            : "default";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Auto Reminder",
      key: "reminderActive",
      width: 130,
      render: (_, record) => (
        <Tooltip title={record.reminderActive ? "Reminder on — click to disable" : "Reminder off — click to enable"}>
          <Switch
            size="small"
            checked={record.reminderActive ?? true}
            loading={togglingReminderId === record.deviceId}
            onChange={(checked) => handleToggleReminder(record.deviceId!, checked)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          {hasPermission(PERMISSIONS.VIEW_DEVICE.name) && (
            <Tooltip title="View Details">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => handleViewDevice(record)}
              />
            </Tooltip>
          )}

          {hasPermission(PERMISSIONS.REGISTER_DEVICE.name) && (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() =>
                  navigate(PERMISSIONS.REGISTER_DEVICE.route, {
                    state: { id: record.id },
                  })
                }
              />
            </Tooltip>
          )}

          {hasPermission(PERMISSIONS.RELEASE_DEVICES.name) && (
            <Tooltip title="Release Device">
              <Popconfirm
                title="Are you sure you want to release this device?"
                onConfirm={() => handleRelease(record.id || 0)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="text"
                  danger
                  icon={<DisconnectOutlined />}
                  loading={releasingId === record.id}
                />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title="Device List"
        extra={
          <Button
            type="primary"
            onClick={() => navigate(PERMISSIONS.REGISTER_DEVICE.route)}
            icon={<PlusOutlined />}
          ></Button>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSearch}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} md={isAdmin ? 12 : 24}>
              <Form.Item>
                <Input
                  title="Filter by Device ID, Customer Name or Phone, Status, Seller ..."
                  placeholder="Filter by Device ID, Customer Name or Phone, Status, Seller ..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onPressEnter={handleSearch}
                  suffix={
                    <Button
                      type="text"
                      icon={<SearchOutlined />}
                      onClick={handleSearch}
                      style={{ height: "100%" }}
                    />
                  }
                  allowClear
                  style={{ height: 32 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item>
                <Select
                  title="Filter by status"
                  placeholder="Filter by status"
                  allowClear
                  style={{ width: "100%" }}
                  onChange={handleStatusChange}
                  value={statusFilter}
                >
                  <Option value="">All</Option>
                  <Option value="PENDING">Pending</Option>
                  <Option value="ACTIVE">Active</Option>
                  <Option value="COMPLETE">Complete</Option>
                  <Option value="LOCKED">Locked</Option>
                </Select>
              </Form.Item>
            </Col>
            {isAdmin && (
              <Col xs={24} md={6}>
                <Form.Item>
                  <Space>
                    <Switch
                      title="Show active/inactive devices"
                      checked={activeOnly}
                      onChange={(checked) => {
                        setActiveOnly(checked);
                        setTimeout(() => {
                          fetchDevices(
                            {
                              ...pagination,
                              current: 1,
                            },
                            checked,
                            searchText,
                            statusFilter
                          );
                        }, 0);
                      }}
                    />
                  </Space>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>

        <Table
          columns={columns}
          dataSource={devices}
          rowKey="id"
          loading={loading}
          scroll={{ x: true }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} devices`,
          }}
          onChange={(pagination) => handleTableChange(pagination)}
        />
      </Card>

      {/* View Device Details Modal */}
      <Modal
        title="Device Details"
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {selectedDevice && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Device ID">
              {selectedDevice.deviceId || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Model">
              {selectedDevice.deviceModel || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="IMEI">
              {selectedDevice.imei1 || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Customer Name">
              {selectedDevice.customerName || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Customer Phone">
              {selectedDevice.customerPhone || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Seller">
              {selectedDevice.sellerOrganization || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Seller Phone">
              {selectedDevice.sellerPhone || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Registered Date">
              {selectedDevice.createdAt || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Last Synced">
              {selectedDevice.lastSync || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag
                color={
                  selectedDevice.status === "ACTIVE"
                    ? "success"
                    : selectedDevice.status === "PENDING"
                    ? "warning"
                    : selectedDevice.status === "LOCKED"
                    ? "error"
                    : "default"
                }
              >
                {selectedDevice.status || "-"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Upcoming EMI">
              {selectedDevice.upcomingEmi ? (
                <Tag color="red">Yes</Tag>
              ) : (
                <Tag color="green">No</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Auto EMI Reminder">
              <Switch
                size="small"
                checked={selectedDevice.reminderActive ?? true}
                loading={togglingReminderId === selectedDevice.deviceId}
                onChange={(checked) =>
                  handleToggleReminder(selectedDevice.deviceId!, checked)
                }
              />
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default DeviceListPage;
